import range from './';
import tacHelpers from '../../tacHelpers';
import productService from '../../../../services/products';
import geometry from '../../../../scene/util/geometry';
import idGenerator from '../../../../util/aactools/idGenerator';
import { ITEMS } from '../../../../constants';

const CROSS_BRACE_ID = '87749600';

function shouldSkipCrossBrace(section, index, movedWasEven) {
  return movedWasEven ? index % 2 === 0 : index % 2 !== 0;
}

function getSectionsPreparedForUpdate(superSection, movedItem = null) {
  /*
  Sort touching sections first by x,
  to decide which should have the cross braces.
  Then sort them again by height,
  to allow higher sections priority for their side panels.
  */
  let movedWasEven;
  const sectionsSorted = superSection
    .filter(product => productService.isSection(product))
    .sort((a, b) => a.x - b.x)
    .map((section, index) => {
      if (movedItem?.itemid === section.itemid) {
        movedWasEven = index % 2 === 0;
        return null;
      }

      return {
        ...section,
        localOptions: {
          partsToSkip: shouldSkipCrossBrace(section, index, movedWasEven)
            ? [
                Object.values(section.parts).find(part =>
                  part.includes(CROSS_BRACE_ID)
                ),
              ]
            : [],
        },
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.height - a.height || a.x - b.x);

  return sectionsSorted.length ? sectionsSorted : [];
}

function replaceSectionsTransformedByMerge(superSection) {
  return superSection
    .filter(product => productService.isSection(product))
    .sort((a, b) => a.x - b.x)
    .map((section, index, allSections) => {
      if (
        allSections[index - 1]?.height > section.height &&
        allSections[index + 1]?.height > section.height
      ) {
        // This section has higher sections on both sides,
        // thereby changing its height
        const newHeight = [allSections[index - 1], allSections[index + 1]].sort(
          (a, b) => a.height - b.height
        )[0].height;

        // New height is equal to the lowest of the enclosing neighbors
        const replacementProduct = productService.getFit(section, {
          height: newHeight,
        });

        const newSection = tacHelpers.getSwitchableItem(
          section,
          replacementProduct
        );

        return newSection;
      }
      return section;
    });
}

/**
 * Splits an array of sections into two arrays of sections based on whether they
 * are located to the left or to the right of a specific section (dividingSection)
 * and returns an array of the resulting left/right arrays.
 * dividingSection may, but does not have to, be present in the input array. Even if
 * present, it will not be included neither in the left nor right arrays, since its x
 * value is neither less than nor greater than its own x value.
 * @param {Array} superSection The input array of sections.
 * @param {object} dividingSection The dividing section to compare to.
 * @returns {Array} The array containing the resulting left and right arrays.
 */
function splitSuperSectionIntoLeftAndRightParts(superSection, dividingSection) {
  const superSectionParts = [];

  const left = superSection.filter(section => section.x < dividingSection.x);
  const right = superSection.filter(section => section.x > dividingSection.x);
  superSectionParts.push(left, right);

  return superSectionParts;
}

/**
 * Gets the caused updates and removals in the sections due to a specific move
 * @param {*} tac
 * @param {*} triggerItem The moved item causing the changes
 * @param {*} isPersistent False if the user is still dragging
 * @returns {Object}
 */
const getSectionChanges = (tac, triggerItem, isPersistent) => {
  let sectionsToUpdate = [];

  const tacItem = tacHelpers.getItem(tac, triggerItem.itemid);
  let currentSuperSection = tacItem && range.findSuperSection(tac, tacItem, []);

  if (currentSuperSection?.length > 1) {
    if (isPersistent) {
      currentSuperSection =
        replaceSectionsTransformedByMerge(currentSuperSection);
    }
    sectionsToUpdate.push(...getSectionsPreparedForUpdate(currentSuperSection));
  }

  const oldSuperSection = range
    .findSuperSection(tac, triggerItem, [])
    .filter(member => {
      return (
        !currentSuperSection ||
        !currentSuperSection.find(
          candidate => candidate.itemid === member.itemid
        )
      );
    });

  const superSectionParts = oldSuperSection
    ? splitSuperSectionIntoLeftAndRightParts(oldSuperSection, triggerItem)
    : [];
  superSectionParts.forEach(superSectionPart => {
    if (superSectionPart.length) {
      sectionsToUpdate.push(
        ...getSectionsPreparedForUpdate(superSectionPart, triggerItem)
      );
    }
  });

  if (!tacItem) {
    sectionsToUpdate = sectionsToUpdate.filter(
      section => section.itemid !== triggerItem.itemid
    );
  }

  return {
    updated: sectionsToUpdate,
    removed: sectionsToUpdate.flatMap(section => {
      // Remove all side panels and cross braces in the sections staged for update,
      // so we can re-assemble them according to the new setup
      return section.items.filter(item =>
        productService.isType(item, ['side-panel', 'cross-brace'])
      );
    }),
  };
};

/**
 * Checks whether a door is in front of a shelf
 * @param {*} door
 * @param {*} shelf
 * @returns {Boolean}
 */
const isDoorInFrontOfShelf = (door, shelf) => {
  return geometry.collides(door, shelf, {
    front: 100,
    back: 100,
  });
};

/**
 * Re-evaluates all doors in the tac and adds references on their shelves
 * @param {*} tac
 * @returns {Object}
 */
const getAllDoorSectionsWithFreshReferencesOnShelves = tac => {
  const sectionsWithDoors = tac.items.filter(section =>
    section.items.find(item => productService.isType(item, ITEMS.DOOR))
  );

  return {
    updated: sectionsWithDoors.map(section => {
      const doors = section.items.filter(item =>
        productService.isType(item, ITEMS.DOOR)
      );

      return {
        ...section,
        items: section.items.map(item => {
          const collidingDoor =
            productService.isType(item, 'shelf') &&
            doors.find(door => isDoorInFrontOfShelf(door, item));

          return {
            ...item,
            ...(collidingDoor ? { belongsTo: collidingDoor.itemid } : {}),
          };
        }),
        localOptions: {
          keepParts: true,
        },
      };
    }),
  };
};

/**
 * Get the slots for shelves behind a specific door
 * @param {*} door
 * @param {*} section
 * @returns {Array}
 */
const getBehindDoorSlots = (door, section) => {
  const fakedTac = { items: [{ ...section }] };
  return tacHelpers
    .getSlots(fakedTac, productService.getFittingShelf(section))
    .filter(slot => isDoorInFrontOfShelf(door, slot.local));
};

/**
 * Gets the section where the door as moved, with added shelves behind the door
 * @param {*} door
 * @param {*} section
 * @param {*} isPersistent
 * @returns {Object}
 */
const getNewSectionWithDoorShelves = (door, section, isPersistent) => {
  const availableSlots = getBehindDoorSlots(door, section);

  if (availableSlots.length < 13) {
    throw new Error('Not possible to place shelves behind door');
  }

  const newShelves = [0, 6, 12].map(index => availableSlots[index].local);

  return {
    ...section,
    items: [
      ...section.items.filter(item => item.belongsTo !== door.itemid),
      ...newShelves.map(shelf => ({
        ...shelf,
        itemid: isPersistent ? idGenerator.id() : idGenerator.fakeId(),
        belongsTo: door.itemid,
      })),
    ],
    localOptions: {
      keepParts: true,
    },
  };
};

/**
 * Gets the old section where the door was placed previously,
 * with the shelves that used to be behind the door removed
 * @param {*} door
 * @param {*} section
 * @returns {Object}
 */
const getOldSectionWithoutDoorShelves = (door, section) => {
  return {
    ...section,
    items: section.items.filter(item => item.belongsTo !== door.itemid),
    localOptions: {
      keepParts: true,
    },
  };
};

/**
 * Gets the caused updates due to a specific door moving in the scene
 * @param {*} tac
 * @param {*} triggerItem
 * @param {*} isPersistent
 * @returns {Object}
 */
const getSpecificDoorChanges = (tac, triggerItem, isPersistent) => {
  const door = tacHelpers.getItem(tac, triggerItem.itemid) || triggerItem;
  const oldParent = tac.items.find(section =>
    section.items.find(item => item.belongsTo === door.itemid)
  );
  const newParent = tacHelpers.getParent(tac, door);
  const hasChangedParent = oldParent && oldParent.itemid !== newParent?.itemid;

  return {
    updated: [
      ...(hasChangedParent
        ? [getOldSectionWithoutDoorShelves(door, oldParent)]
        : []),
      ...(newParent
        ? [getNewSectionWithDoorShelves(door, newParent, isPersistent)]
        : []),
    ],
  };
};

/**
 * Gets all the dependency changes caused by a change in the TAC
 * @param {*} tac
 * @param {*} options
 * @returns {Object}
 */
function getDependencyDiff(tac, options = {}) {
  const { isPersistent, triggerItem } = options;

  const diff = { added: [], updated: [], removed: [], untouched: [] };

  if (triggerItem) {
    if (productService.isType(triggerItem, ITEMS.DOOR)) {
      return {
        ...diff,
        ...getSpecificDoorChanges(tac, triggerItem, isPersistent),
      };
    }
    if (productService.isSection(triggerItem)) {
      return { ...diff, ...getSectionChanges(tac, triggerItem, isPersistent) };
    }
  } else if (isPersistent) {
    return { ...diff, ...getAllDoorSectionsWithFreshReferencesOnShelves(tac) };
  }
}

export default { getDependencyDiff };

export { getDependencyDiff };
